import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { companiesApi } from '../services/api/companies';
import type { DashboardStatistics } from '../types/dashboard';

// Server-side page size for the recent-activity feed. The backend defaults to
// 10 as well; sending it explicitly keeps the request and the cache key in sync
// with what is displayed.
const RECENT_ACTIVITY_PER_PAGE = 10;

export interface UseAdminDashboardReturn {
  stats: DashboardStatistics | null;
  loading: boolean;
  error: string | null;
  isRecentActivityFetching: boolean;
  recentActivityPage: number;
  setRecentActivityPage: (page: number) => void;
  isMonthModalOpen: boolean;
  setIsMonthModalOpen: (open: boolean) => void;
  openMonthModal: () => void;
  goToCompanies: () => void;
  goToClients: () => void;
  goToPendingQuotes: () => void;
  goToQuote: (quoteId: number) => void;
}

export const useAdminDashboard = (): UseAdminDashboardReturn => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isMonthModalOpen, setIsMonthModalOpen] = useState(false);
  const [recentActivityPage, setRecentActivityPage] = useState(1);

  const { data, isPending, isFetching, error } = useQuery({
    // Each page is its own cache entry, so returning to a previously viewed
    // page is instant (within staleTime) instead of refetching.
    queryKey: [
      'admin',
      'dashboard',
      'statistics',
      {
        recent_activity_page: recentActivityPage,
        recent_activity_per_page: RECENT_ACTIVITY_PER_PAGE,
      },
    ],
    queryFn: () =>
      companiesApi.getAdminDashboardStatistics({
        recent_activity_page: recentActivityPage,
        recent_activity_per_page: RECENT_ACTIVITY_PER_PAGE,
      }),
    staleTime: 30_000,
    // Keep the current page visible while the next page loads instead of
    // collapsing the whole dashboard back to the skeleton.
    placeholderData: keepPreviousData,
  });

  // The backend echoes the requested page even when it is out of range (e.g.
  // the feed shrank between requests, or the page was deep-linked), returning
  // the requested `current_page` with an empty array. Snap back to the last
  // valid page so the feed never rests on a misleading empty page. An empty
  // feed (`total_pages === 0`) is left alone — page 1 is the only valid page.
  const pagination = data?.statistics?.recent_activities_pagination;
  useEffect(() => {
    if (!pagination) return;
    const lastValidPage = Math.max(1, pagination.total_pages);
    if (recentActivityPage > lastValidPage) {
      setRecentActivityPage(lastValidPage);
    }
  }, [pagination, recentActivityPage]);

  return {
    stats: data?.statistics ?? null,
    loading: isPending,
    error: error ? t('admin.dashboard.failed_to_load') : null,
    isRecentActivityFetching: isFetching,
    recentActivityPage,
    setRecentActivityPage,
    isMonthModalOpen,
    setIsMonthModalOpen,
    openMonthModal: () => setIsMonthModalOpen(true),
    goToCompanies: () => navigate('/admin/companies'),
    goToClients: () => navigate('/admin/clients'),
    goToPendingQuotes: () => navigate('/admin/quotes?status=pending'),
    goToQuote: (quoteId: number) => navigate(`/admin/quotes/${quoteId}`),
  };
};
